import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useContext } from 'react';
import type { ClearanceDetail } from '../../../../../js/api';
import { ConnectionsService } from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { Button } from '../../../components/button';
import { TooltippedText } from '../../../components/tooltipped-text';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { ORGANIZATION_CLEARANCE_LIST_QUERY_KEY, CLEARANCE_QUERY_KEY } from './queryKeys';

type ClearanceQuickDetailsProps = {
  clearance: ClearanceDetail;
};

const DATE_FORMAT = 'LLL d, yyyy';

export function ClearanceQuickDetails({ clearance }: ClearanceQuickDetailsProps) {
  const queryClient = useQueryClient();
  const { data: specialtyMap = {} } = useCodeDefinitionsAsMap('Specialty');
  const provider = clearance.provider;
  const dob = provider.birth_date ? format(parseISO(provider.birth_date), DATE_FORMAT) : '--';
  const npi = provider.npi || '--';
  const specialtiesList =
    provider.specialties?.length && provider.specialties.length > 0
      ? provider.specialties.map((code) => ({
          key: code,
          label: specialtyMap[code] || code,
        }))
      : [{ key: 'none', label: '--' }];
  const { user } = useContext(SessionContext);

  const revealNameMutation = useMutation({
    mutationFn: async () => {
      return ConnectionsService.connectionsClearanceUpdate({
        id: clearance.id,
        requestBody: { reveal_name: true } as ClearanceDetail,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_CLEARANCE_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CLEARANCE_QUERY_KEY, clearance.id],
      });
    },
  });

  return (
    <div className="tw:bg-brand-green-0/10 tw:rounded-lg tw:p-4 tw:text-zinc-700 tw:leading-tight">
      {clearance.reveal_name ? (
        <div className="tw:grid tw:grid-cols-2 tw:gap-x-4 tw:gap-y-3 tw:items-center">
          {/* DOB and NPI - left side */}
          <div className="tw:flex tw:flex-col tw:gap-2">
            <div className="tw:flex tw:items-center tw:gap-2">
              <TooltippedText tooltipContent="Date of birth">
                <i className="fa-solid fa-fw fa-calendar tw:text-brand-green-0 tw:shrink-0 tw:mr-2 fa-fw" />
                <span className="tw:truncate">{dob}</span>
              </TooltippedText>
            </div>
            <div className="tw:flex tw:items-center tw:gap-2">
              <TooltippedText tooltipContent="National Provider Identifier">
                <i className="fa-solid fa-fw fa-id-card tw:text-brand-green-0 tw:shrink-0 tw:mr-2 fa-fw" />
                <span className="tw:truncate">{npi}</span>
              </TooltippedText>
            </div>
          </div>

          {/* Specialties - right side */}
          <div className="tw:flex tw:gap-2 tw:items-start">
            <i className="fa-solid fa-stethoscope tw:text-brand-green-0 tw:shrink-0 tw:mt-0.5 fa-fw" />
            <ul className="tw:flex tw:flex-col tw:gap-1 tw:list-none tw:p-0 tw:m-0 tw:!pl-0">
              {specialtiesList.map(({ key, label }) => (
                <li key={key} className="tw:truncate">
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <>
          {user?.permissions.includes('update__connections__clearance') && (
            <div className="tw:flex tw:flex-row tw:gap-5 tw:items-center">
              <Button color="brandgreen0" onClick={() => revealNameMutation.mutate()}>
                Reveal Name
              </Button>
              <div className="tw:text-brand-green-1 tw:text-xs/3">
                Reveal name to proceed with clearance.
                <br />
                If you do not wish to proceed, select connection below and withdraw.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
